import React, { forwardRef } from 'react';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import makeStyles from '@mui/styles/makeStyles';
import { Spotlight } from '@sis-cc/dotstatsuite-visions/Spotlight';
import SisccAppBar from './visions/SisccAppBar';
import DeAppBar from './visions/DeAppBar';
import { FormattedMessage, formatMessage } from '../i18n';
import { useAppNavigation } from '../hooks/navigation/useAppNavigation';
import { useSearchNavigation } from '../hooks/navigation/useSearchNavigation';
import { useSdmxNavigation } from '../hooks/navigation/useSdmxNavigation';
import {
  useLocale,
  useIsRtl,
  usePathname,
  useTerm,
  useSearchResultNb,
  useIsBypass,
} from '../hooks/router';
import { getHasNoSearchParams } from '../selectors/search';
import { getAsset, getApp, locales } from '../lib/settings';
import messages from './messages';
import { ID_HEADER } from '../css-api';

const useStyles = makeStyles(() => ({
  container: {
    width: '100%',
    zIndex: 0,
  },
}));

const TopBar = ({ isRtl, localeId }) => {
  const { changeLocale } = useAppNavigation();
  return (
    <SisccAppBar
      logo={getAsset('header', localeId)}
      locales={Object.keys(locales)}
      logoLink={getApp('headerLink', localeId)}
      localeId={localeId}
      isRtl={isRtl}
      changeLocale={changeLocale}
    />
  );
};

const BottomBar = ({ isVis, isRtl, localeId }) => {
  const { changeTerm, resetSearch } = useSearchNavigation();
  const { resetDataflow } = useSdmxNavigation();
  const hasNoSearchParams = useSelector(getHasNoSearchParams);
  const searchResultNb = useSearchResultNb();
  const bypass = useIsBypass();
  const term = useTerm();
  const intl = useIntl();
  if (hasNoSearchParams && !isVis) {
    return null;
  }
  const logo = getAsset('subheader', localeId);
  const goHome = resetSearch;
  const goBackSearch = !isVis
    ? null
    : hasNoSearchParams || (searchResultNb <= 1 && bypass && !term)
      ? goHome
      : resetDataflow;

  return (
    <DeAppBar
      logo={logo}
      goHome={goHome}
      goBack={goBackSearch}
      goBackLabel={<FormattedMessage id="de.search.back" />}
    >
      {!hasNoSearchParams && !isVis && (
        <Spotlight
          hasClearAll
          hasCommit
          action={changeTerm}
          placeholder={formatMessage(intl)(messages.placeholder)}
          isRtl={isRtl}
          term={term}
          labels={{
            searchSpotlight: formatMessage(intl)(messages.searchSpotlight),
            clearSpotlight: formatMessage(intl)(messages.clearSpotlight),
            commitSpotlight: formatMessage(intl)(messages.commitSpotlight),
          }}
        />
      )}
    </DeAppBar>
  );
};

const AppBars = (_, ref) => {
  const isVis = usePathname() === '/vis';
  const isRtl = useIsRtl();
  const classes = useStyles();
  const localeId = useLocale();

  return (
    <header
      id={ID_HEADER}
      className={classes.container}
      style={{ position: isVis ? 'fixed' : 'static' }}
      ref={ref}
    >
      <>
        <TopBar isRtl={isRtl} localeId={localeId} />
        <BottomBar isVis={isVis} isRtl={isRtl} localeId={localeId} />
      </>
    </header>
  );
};

export default forwardRef(AppBars);
